import type { CellValue } from 'hyperformula';
import type { CellSelection } from '../../../../common/table/TableTypes';
import { getColumnCount, getRowCount } from '../../../../common/table/utils/tableCellDataPropertyUtils';

import HotTableClipboardSourceManager from '../manager/HotTableClipboardSourceManager';
import { getAllCellsBetween } from '../utils/selectionUtils/tableSelectionGeometryUtils';
import { getCellSelectionFromRange } from '../utils/selectionUtils/tableSelectionRangeUtils';
import { MilanoteHotPlugin } from './MilanoteHotPlugin';

export const COPY_PASTE_EXTENSION_PLUGIN_NAME = 'CopyPasteExtension';

type HotCopyPasteSelection = {
    startRow: number;
    startCol: number;
    endRow: number;
    endCol: number;
};
type HotAutofillRange = {
    from: {
        row: number;
        col: number;
    };
    to: {
        row: number;
        col: number;
    };
};
/**
 * This is an addition to Handsontable CopyPaste plugin that we are already using, which only
 * copy/cut/paste cell primitive data.
 *
 * This plugin keeps a record of the cell metadata of copied/cut/autofill origin cells, and paste them over
 * to the destination cells. This is so that things like cell type and cell styling gets copied over to the cells.
 */
export default class CopyPasteExtensionHotPlugin extends MilanoteHotPlugin {
    static get PLUGIN_KEY(): string {
        return COPY_PASTE_EXTENSION_PLUGIN_NAME;
    }

    enablePlugin(): void {
        this.addHook('beforeAutofill', (_selectionData, sourceRange) => this.onBeforeAutofill(sourceRange));
        this.addHook('beforeCopy', (_data, coords) => this.onBeforeCopy(coords));
        this.addHook('beforeCut', (_data, coords) => this.onBeforeCut(coords));
        this.addHook('beforePaste', (data, coords) => this.onBeforePaste(data, coords));

        super.enablePlugin();
    }

    /**
     * Captures the source range before autofill runs so the fill operation can reuse the original cell data.
     */
    onBeforeAutofill(sourceRange: HotAutofillRange): void {
        HotTableClipboardSourceManager.setTableAutofillSource(this.milanoteHot, getCellSelectionFromRange(sourceRange));
    }

    /**
     * Records the current copy selection so later paste flows can recover the copied table cell data.
     */
    onBeforeCopy(coords: Array<HotCopyPasteSelection | undefined>): void {
        const selection = coords[0];
        if (!selection) return;

        const { startRow, startCol, endRow, endCol } = selection;
        HotTableClipboardSourceManager.setTableCopySource(this.milanoteHot, [startRow, startCol, endRow, endCol]);
    }

    /**
     * Reuses the copy bookkeeping for cut operations, then temporarily clears read-only flags so HOT can remove
     * the cut cell contents.
     */
    onBeforeCut(coords: Array<HotCopyPasteSelection | undefined>): void {
        const selection = coords[0];
        if (!selection) return;

        this.onBeforeCopy(coords);

        const { startRow, startCol, endRow, endCol } = selection;
        getAllCellsBetween([[startRow, startCol, endRow, endCol]]).forEach(({ row, col }) => {
            this.milanoteHot.setCellMetaObject(row, col, { readOnly: false });
        });
    }

    /**
     * Makes the destination range editable before paste so protected cell metadata does not block the incoming data.
     */
    onBeforePaste(data: CellValue[][], coords: Array<HotCopyPasteSelection | undefined>): void {
        const selection = coords[0];
        if (!selection) return;

        const { startRow, startCol, endRow, endCol } = selection;
        const pastedRowCount = getRowCount(data);
        const pastedColumnCount = getColumnCount(data);

        const checkboxRendererVersion = Date.now();

        const pastedCellSelection: CellSelection = [
            startRow,
            startCol,
            Math.max(endRow, startRow + pastedRowCount - 1),
            Math.max(endCol, startCol + pastedColumnCount - 1),
        ];
        getAllCellsBetween([pastedCellSelection]).forEach(({ row, col }) => {
            this.milanoteHot.setCellMetaObject(row, col, { readOnly: false, checkboxRendererVersion });
        });
    }
}
